import { Vec2 } from '../../math/vec2.js';
import { SeatingLabel } from './seating-label.js';
import { SeatingElement } from './seating-element.js';
import { SeatRow } from './seat-row.js';
import { Seat } from './seat.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData, isSameList, SerializedData } from '../../dto/dto.js';
import { StandingArea } from './standing-area.js';
import { JSONUtils } from '../../../utility/json-utils.js';

export class Sector extends SeatingElement {
  static readonly TYPE_ID: number = 0x03;

  public readonly children: SeatingElement[];
  label: SeatingLabel | null;

  constructor(
      uniqueId: number,
      enabled: boolean,
      position: Vec2,
      rotation: number,
      label: SeatingLabel | null,
      children: SeatingElement[],
      parent: WeakRef<SeatingElement> | null = null,
  ) {
    super(uniqueId, enabled, position, rotation, parent);
    this.label = label;
    this.children = children;
  }

  get $typeId(): number {
    return Sector.TYPE_ID;
  }

  static fromJson(data: SerializedData): Sector {
    const decodeChild = (childData: SerializedData): SeatingElement => {
      const typeId = MapUtils.get<number>(childData, '$typeId');
      switch(typeId) {
        case Seat.TYPE_ID:
          return Seat.fromJson(childData);
        case SeatRow.TYPE_ID:
          return SeatRow.fromJson(childData);
        case Sector.TYPE_ID:
          return Sector.fromJson(childData);
        case SeatingLabel.TYPE_ID:
          return SeatingLabel.fromJson(childData);
        case StandingArea.TYPE_ID:
          return StandingArea.fromJson(childData);
      }

      throw Error(`Unknown SeatingElement of type: ${typeId.toString(16)}`);
    };

    const result = new Sector(
        MapUtils.getInteger(data, 'uniqueId'),
        MapUtils.getBoolean(data, 'enabled'),
        Vec2.fromJson(MapUtils.getRecord(data, 'position')),
        MapUtils.getDouble(data, 'rotation'),
        SeatingLabel.fromJsonOrNull(MapUtils.getRecordOrNull(data, 'label')),
        MapUtils.getList(data, 'children', (x) => decodeChild(JSONUtils.decodeRecord(x))),
    );

    result.label?.setParent(result);
    for(const child of result.children) {
      child.setParent(result);
    }

    return result;
  }

  toJson(): SerializedData {
    return {
      '$typeId': this.$typeId,
      'uniqueId': this.uniqueId,
      'enabled': this.enabled,
      'position': this.position.toJson(),
      'rotation': this.rotation,
      'label': this.label?.toJson() ?? null,
      'children': this.children.map((child) => child.toJson()),
    };
  }

  clone(parent: WeakRef<SeatingElement> | null): this {
    const result = new Sector(
        this.uniqueId,
        this.enabled,
        this.position.clone(),
        this.rotation,
        this.label?.clone(null) ?? null,
        this.children.map((child) => child.clone(null)),
        parent,
    ) as this;

    result.label?.setParent(result);
    for(const child of result.children) {
      child.setParent(result);
    }

    return result;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.enabled === other.enabled &&
        isSameData(this.position, other.position) &&
        this.rotation === other.rotation &&
        isSameData(this.label, other.label) &&
        isSameList(this.children, other.children, (a, b) => isSameData(a, b)) &&
        this.parent === other.parent;
  }
}
